import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { ChangeSalesPriceModal } from "@/components/admin/shared/ChangeSalesPriceModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  AddButton,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
} from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput, PennDropdown } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import {
  useSherexOption,
  useSherexOptionGroups,
  useSherexOptions,
  useSherexPricePreview,
  useSherexPriceProducts,
  useCreateSherexOption,
  useDeleteSherexOption,
  useUpdateSherexOption,
} from "@/hooks/useSherexOptions";
import {
  useApplyDatasetPriceUpdate,
  useDatasetPriceUpdateSummary,
} from "@/hooks/usePriceUpdate";
import type {
  SherexIdPattern,
  SherexOption,
  SherexOptionRegion,
  UpdateSherexOptionBody,
} from "@/api/sherexOptions";
import { CURRENCY_SYMBOL, formatMoney, type Currency } from "@/utils/dataset-money";
import { Percent } from "lucide-react";

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

/**
 * Both currencies live on the SAME shared sherex_pricing row; this page is
 * mounted once per currency (EU route -> price_eur, US route -> price_usd)
 * exactly like Autoriv Configurator Items. Everything except the price
 * column/format is shared between the two mounts.
 *
 * The OPTION ROWS are shared as well: both mounts list the FULL catalog, EU
 * and NA rows together, so `pagination.total` is the whole active set on either
 * page. `region` remains a stored column — a row added here is still filed
 * under this mount's region and can never move — but it no longer scopes what
 * is listed, opened, edited or deleted. The only thing still split per mount is
 * the price column this page reads and writes.
 */
const CURRENCY_META: Record<
  Currency,
  {
    symbol: string;
    label: string;
    /** Region this mount belongs to — currency travels via region (§D.14). */
    region: SherexOptionRegion;
    /** `region.region_id` the bulk price change is scoped to. */
    regionId: number;
  }
> = {
  // Separators are NOT configured here — `formatMoney` renders both currencies
  // en-US style ("€1,835.40") at exactly 2 decimals.
  EUR: { symbol: CURRENCY_SYMBOL.EUR, label: "Price (EUR)", region: "EU", regionId: 1 },
  USD: { symbol: CURRENCY_SYMBOL.USD, label: "Price (USD)", region: "NA", regionId: 2 },
};

const optionFormSchema = z.object({
  /**
   * NOT trimmed — the id is stored exactly as typed. Sherex part numbers carry
   * significant spacing ("DLHT 1/2-13 Set Up", "LHF 202M (Metric Tool)"), so the
   * form must not quietly rewrite what was entered. Blank-only is still
   * rejected; the per-group FORMAT check is separate (`idPatternError`), since
   * its rule arrives from svc-04 rather than living in this schema.
   */
  optionId: z
    .string()
    .min(1, "ID / Part No is required")
    .max(30, "ID / Part No must be 30 characters or fewer")
    .refine((v) => v.trim().length > 0, "ID / Part No is required"),
  productGroup: z.string().trim().min(1, "Product Group is required").max(20),
  threadSize: z.string().trim().max(20).optional(),
  installationMethod: z.string().trim().max(30).optional(),
  pullingForce: z.string().trim().max(30).optional(),
  availableStroke: z.string().trim().max(30).optional(),
  airPressure: z.string().trim().max(50).optional(),
  airUse: z.string().trim().max(30).optional(),
  toolSpeed: z.string().trim().max(20).optional(),
  weight: z.string().trim().max(30).optional(),
  completeHeadSetAssemblyPn: z.string().trim().max(30).optional(),
  mandrelPn: z.string().trim().max(20).optional(),
  anvilPn: z.string().trim().max(20).optional(),
  completeSetUps: z.string().trim().max(20).optional(),
  includes: z.string().trim().max(100).optional(),
  /**
   * A cleared number input reports `""`, which `z.coerce.number` turns into a
   * silent 0 — and 0 on a SHARED pricing row wipes the price for every sibling
   * option. Empty is mapped to `undefined` so it fails as "required" instead.
   */
  price: z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    z.coerce
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .nonnegative("Must be greater than or equal to 0"),
  ),
});
type OptionFormData = z.infer<typeof optionFormSchema>;

/**
 * One selectable entry in the Product Pricing dropdown: a pricing product
 * paired with the id stem that reaches it.
 *
 * Both come from svc-04 (`GET /price-products`). A product with several
 * published stems (key `FLEX5` is reachable as `FLEX-5 `, `FLEX-5P ` and
 * `FLEX-5S `) is offered once PER STEM — they price identically but produce
 * different ids, and only the admin knows which tool is being added.
 */
type PricingChoice = {
  /** Dropdown value, and the prefix locked in front of the part number. */
  stem: string;
  /** `sherex_pricing.product_name` this stem resolves to. */
  productName: string;
  optionCount: number;
  priceEur: string | null;
  priceUsd: string | null;
};

const blank: OptionFormData = {
  optionId: "",
  productGroup: "",
  threadSize: "",
  installationMethod: "",
  pullingForce: "",
  availableStroke: "",
  airPressure: "",
  airUse: "",
  toolSpeed: "",
  weight: "",
  completeHeadSetAssemblyPn: "",
  mandrelPn: "",
  anvilPn: "",
  completeSetUps: "",
  includes: "",
  price: 0,
};

/**
 * Does `optionId` sit under one of its group's published stems, with a part
 * number after it?
 *
 * The stems arrive from svc-04 (`GET /groups` -> `idPatterns`), so this APPLIES
 * the server's rule rather than restating it — the same table rejects the POST
 * with `sherex-option-id-pattern-mismatch` if the browser is bypassed (§L, §R —
 * no client-only business rules). Case-insensitive to match
 * `getSherexPricingKey`, which upper-cases before resolving; the id itself is
 * still stored exactly as typed.
 */
const matchesIdPattern = (optionId: string, pattern: SherexIdPattern): boolean => {
  const id = optionId.toUpperCase();
  return pattern.prefixes.some(
    (prefix) =>
      id.startsWith(prefix.toUpperCase()) &&
      id.slice(prefix.length).trim().length > 0,
  );
};

const ProductGroupBadge = ({ value }: { value: string | null }) => {
  if (!value) return <>—</>;
  return (
    <span className="inline-flex items-center rounded-full bg-purple-50 dark:bg-purple-900/30 px-3 py-0.5 text-purple-700 dark:text-purple-300 text-small-medium">
      {value}
    </span>
  );
};

const InstallMethodBadge = ({ value }: { value: string | null }) => {
  if (!value) return <>—</>;
  return (
    <span className="inline-flex items-center rounded-full bg-amber-50 dark:bg-amber-900/30 px-3 py-0.5 text-amber-700 dark:text-amber-300 text-small-medium">
      {value}
    </span>
  );
};

/**
 * Debounces a value by `delay` ms — used to throttle the price-group preview
 * lookup while the admin is still typing the ID / picking a group.
 */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

/**
 * Shared-pricing warning shown under the Price field. sherex_options has NO
 * FK to sherex_pricing — many options resolve to one price row (rule
 * documented in sherex-options.repository.ts), so saving a price here can
 * change what OTHER options show too. This makes the blast radius visible
 * before Save, using the live optionId + productGroup form values.
 */
const SharedPriceNote = ({
  preview,
  currency,
}: {
  preview: ReturnType<typeof useSherexPricePreview>;
  currency: Currency;
}) => {
  if (!preview.data && !preview.isLoading) return null;
  if (preview.isLoading) {
    return (
      <p className="mt-2 text-blue_grey4 text-small">Checking pricing group…</p>
    );
  }
  if (!preview.data) return null;

  const { priceGroupKey, sharedWithCount } = preview.data;
  const existingPrice = currency === "USD" ? preview.data.priceUsd : preview.data.priceEur;
  if (sharedWithCount === 0) {
    // The picker only offers products that options already use, so landing
    // here means the part number broke the stem (a letter straight after a
    // FLEX stem) and the id resolved somewhere nobody prices. Say so plainly.
    return (
      <p className="mt-2 text-amber-600 dark:text-amber-400 text-small">
        This will CREATE a new pricing product "{priceGroupKey}" — no existing
        option uses it.{" "}
        {existingPrice
          ? `It already carries ${formatMoney(existingPrice, currency)}; saving replaces that.`
          : "Check the part number — it should join the product selected above."}
      </p>
    );
  }
  return (
    <p className="mt-2 text-amber-600 dark:text-amber-400 text-small">
      This price is shared with <strong>{sharedWithCount}</strong> other option
      {sharedWithCount === 1 ? "" : "s"} in pricing group "{priceGroupKey}"
      {existingPrice ? ` (currently ${formatMoney(existingPrice, currency)})` : ""}. 
      Changing the price here will be applied to all the other <strong>{sharedWithCount}</strong> options as well.
    </p>
  );
};

export const SherexOptionsPage = ({
  title = "Sherex Options (EU)",
  subtitle = "Dataset: Sherex Options (EU)",
  currency = "EUR",
}: {
  title?: string;
  subtitle?: string;
  /** Which price column this mount reads and writes. EUR is the default. */
  currency?: Currency;
}) => {
  const priceKey = currency === "USD" ? "priceUsd" : "priceEur";
  const {
    label: priceLabel,
    symbol: currencySymbol,
    region: regionName,
    regionId,
  } = CURRENCY_META[currency];
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  // No `region` filter: both mounts list the WHOLE sherex_options catalog.
  // `region` stays a stored column (the create body below still files a new row
  // under the page it was added on), but it no longer narrows what is shown.
  const params = useMemo(
    () => ({
      page: pageIndex + 1,
      limit: pageSize,
      search: search || undefined,
    }),
    [pageIndex, pageSize, search],
  );

  const { data, isLoading, isFetching, isError, error } = useSherexOptions(params);
  const rows: SherexOption[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const groupsQuery = useSherexOptionGroups();
  const groupOptions = groupsQuery.data?.groups ?? [];
  /** Server-published ID / Part No format per group — see `matchesIdPattern`. */
  const idPatterns = useMemo(
    () => groupsQuery.data?.idPatterns ?? [],
    [groupsQuery.data],
  );

  type ModalMode = "add" | "edit" | "view";
  type ModalState = { mode: ModalMode; item?: SherexOption } | null;

  const [modal, setModal] = useState<ModalState>(null);
  /**
   * Add-modal id builder. The admin picks an EXISTING pricing product
   * (`pricingChoice` holds its id stem) and types only the part number after
   * it; `optionId` in the form is always the two concatenated.
   */
  const [pricingChoice, setPricingChoice] = useState("");
  /** The number typed after the locked stem. */
  const [partNumber, setPartNumber] = useState("");
  /**
   * Required-field messages stay hidden until Save is pressed — an untouched
   * Add form shouting "required" at every field is noise. FORMAT errors are
   * live regardless, since they only appear once something was typed.
   */
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SherexOption | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Bulk "% change bulk price" dialog state. The change scales sherex_pricing
  // rows — NOT option rows — so the count below is pricing products, matching
  // the shared-price rule the single-record modal already warns about. Only
  // this mount's currency column moves; history goes to price_update_log.
  const [priceChangeOpen, setPriceChangeOpen] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const priceSummary = useDatasetPriceUpdateSummary(
    "sherex-options",
    regionId,
    priceChangeOpen,
  );
  const applyPriceMutation = useApplyDatasetPriceUpdate("sherex-options");

  const applyPriceChange = (percentage: number, direction: "increase" | "decrease") => {
    setBulkError(null);
    applyPriceMutation.mutate(
      { percentage, direction, regionId },
      {
        onSuccess: () => setPriceChangeOpen(false),
        onError: (e) => setBulkError(apiErrorMessage(e, "Failed to apply price change.")),
      },
    );
  };

  const editingId = modal?.mode !== "add" ? modal?.item?.optionId ?? null : null;
  // Unscoped by id: the list is no longer region-filtered, so a row from either
  // catalog can be opened here and must not 404 on a region mismatch.
  const detail = useSherexOption(editingId);
  const createMutation = useCreateSherexOption();
  const updateMutation = useUpdateSherexOption();
  const deleteMutation = useDeleteSherexOption();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<OptionFormData>({ resolver: zodResolver(optionFormSchema) });

  const watchedOptionId = watch("optionId");
  const watchedProductGroup = watch("productGroup");

  const isView = modal?.mode === "view";
  const isAdd = modal?.mode === "add";

  /**
   * Product Group gates the rest of the form.
   *
   * Everything downstream of it is group-derived — the ID / Part No format is
   * the group's, and the pricing row this option reads/writes is resolved from
   * (optionId, productGroup). Letting the admin fill the body of the form first
   * and pick a group last is how rows end up in the wrong pricing group, so
   * nothing else unlocks until a group is chosen.
   */
  const selectedGroup = (watchedProductGroup ?? "").trim();
  const groupSelected = selectedGroup.length > 0;
  const fieldsDisabled = isView || !groupSelected;

  /** The selected group's id rule, or null when it publishes none. */
  const activeIdPattern = useMemo(
    () => idPatterns.find((p) => p.productGroup === selectedGroup) ?? null,
    [idPatterns, selectedGroup],
  );

  /**
   * The pricing products of the selected group — the Product Pricing dropdown.
   * Add-only: edit/view can change neither the id nor the group, so the pricing
   * row an existing option reads is fixed.
   */
  const priceProductsQuery = useSherexPriceProducts(
    isAdd && groupSelected ? selectedGroup : null,
  );
  const priceProducts = useMemo(
    () => priceProductsQuery.data?.products ?? [],
    [priceProductsQuery.data],
  );

  /**
   * One dropdown entry per (pricing product × published stem), for the
   * products that options ACTUALLY use.
   *
   * The picker offers existing data only — there is no "new pricing product"
   * path here, so an option can only ever join a row the catalog already
   * carries. svc-04 also returns priced-but-empty rows (`SSG-806`, €999.3998,
   * 0 options) because they are real `sherex_pricing` rows; as a "join this
   * product" choice they are noise, so they are filtered out. Opening a NEW
   * pricing product is therefore not something this modal does — that needs
   * the pricing row to exist and hold options first.
   */
  const pricingChoices = useMemo<PricingChoice[]>(() => {
    const choices: PricingChoice[] = [];
    const seen = new Set<string>();
    for (const product of priceProducts) {
      if (product.optionCount === 0) continue;
      // `idPrefixes` is the server's stem→product pairing; `idPrefix` is the
      // older single-prefix field, kept as the fallback for a group that
      // publishes no format (and for a svc-04 that predates the pairing).
      const stems = product.idPrefixes?.length ? product.idPrefixes : [product.idPrefix];
      for (const stem of stems) {
        if (!stem || seen.has(stem.toUpperCase())) continue;
        seen.add(stem.toUpperCase());
        choices.push({
          stem,
          productName: product.productName,
          optionCount: product.optionCount,
          priceEur: product.priceEur,
          priceUsd: product.priceUsd,
        });
      }
    }
    return choices.sort((a, b) => a.stem.localeCompare(b.stem));
  }, [priceProducts]);

  /** The prefix locked in front of the part number — the picked product's. */
  const activeStem = pricingChoice;
  const stemFilled = activeStem.trim().length > 0;
  const selectedChoice = useMemo(
    () => pricingChoices.find((c) => c.stem === pricingChoice) ?? null,
    [pricingChoices, pricingChoice],
  );

  /**
   * Add-only. Edit/view can change neither the id (immutable PK) nor the group,
   * so re-checking there could only strip the Product Name off a legacy row
   * that predates the rule.
   */
  const idSatisfiesPattern = (value: string): boolean =>
    !isAdd || !activeIdPattern || matchesIdPattern(value, activeIdPattern);

  const typedOptionId = watchedOptionId ?? "";

  /**
   * The id the form actually submits: locked stem + typed part number. Written
   * back into RHF so validation, the price preview and the POST all keep
   * reading ONE value — the two inputs are a keyboard convenience, not a second
   * source of truth.
   */
  useEffect(() => {
    if (!isAdd) return;
    setValue("optionId", `${activeStem}${partNumber}`, { shouldValidate: false });
  }, [isAdd, activeStem, partNumber, setValue]);

  /**
   * Shown under the field and blocks Save. Held back until a part number is
   * typed — a bare stem is the "required" case, not a format complaint, and
   * shouting the format at an untouched field is noise.
   */
  const idPatternError =
    activeIdPattern && partNumber.trim().length > 0 && !idSatisfiesPattern(typedOptionId)
      ? `${activeIdPattern.description}. Example: ${activeIdPattern.example}`
      : null;

  // One shared price-group lookup for BOTH the warning note and the Add-modal
  // prefill below (same query key -> TanStack Query dedupes to one request).
  //
  // Gated on the format check: the resolved Product Name is only meaningful for
  // an id that actually reaches its group's pricing product, so an id that
  // fails the rule resolves nothing and the read-only Product Name stays empty
  // until it passes.
  const debouncedOptionId = useDebounced(typedOptionId, 300);
  const debouncedProductGroup = useDebounced((watchedProductGroup ?? "").trim(), 300);
  // Checked RAW, sent trimmed (svc-04 trims the query param anyway). Checking a
  // trimmed value would let " SSG-801-0440" resolve a Product Name that Save —
  // and the POST, which stores the id verbatim — then refuses.
  const previewId = idSatisfiesPattern(debouncedOptionId)
    ? debouncedOptionId.trim()
    : "";
  const pricePreview = useSherexPricePreview(
    modal && modal.mode !== "view" && previewId ? previewId : null,
    modal && modal.mode !== "view" && debouncedProductGroup ? debouncedProductGroup : null,
  );

  /**
   * Add-modal price prefill — SAFETY, not convenience.
   *
   * The price field defaults to 0, but a new option under an EXISTING pricing
   * group writes to that group's shared sherex_pricing row. Saving with the
   * untouched 0 would zero the price for every sibling option (and for the
   * live customer configurator, which reads the same row). Seeding the field
   * with the group's current price makes "save without touching price" a
   * no-op instead of a wipe. Skipped once the admin edits the field, so a
   * deliberate price change is never clobbered.
   */
  useEffect(() => {
    if (modal?.mode !== "add") return;
    if (dirtyFields.price) return;
    // The preview is the live resolved row, so it wins; the dropdown's own
    // price seeds the field the moment a product is picked, before a part
    // number exists to resolve anything.
    const previewPrice =
      currency === "USD" ? pricePreview.data?.priceUsd : pricePreview.data?.priceEur;
    const choicePrice =
      currency === "USD" ? selectedChoice?.priceUsd : selectedChoice?.priceEur;
    const existing = previewPrice ?? choicePrice;
    if (existing == null) return;
    setValue("price", Number(existing));
  }, [
    modal?.mode,
    dirtyFields.price,
    pricePreview.data,
    selectedChoice,
    currency,
    setValue,
  ]);

  /**
   * Is the preview describing the id currently on screen? The lookup is
   * debounced, so mid-typing it still answers for the PREVIOUS id — reporting a
   * mismatch off that would blink an error at every keystroke.
   */
  const previewFresh =
    debouncedOptionId === typedOptionId && !pricePreview.isFetching;
  /** `sherex_pricing.product_name` svc-04 resolves the composed id to. */
  const resolvedKey = previewFresh ? pricePreview.data?.priceGroupKey ?? "" : "";

  // ---------------------------------------------------------------------
  // Add-modal validation. Required-field messages wait for a Save attempt;
  // format messages are live. Every one of them also blocks Save, and
  // `onSubmit` re-checks — svc-04 rejects the same cases regardless (§L).
  // ---------------------------------------------------------------------
  const pricingRequiredError =
    isAdd && !pricingChoice ? "Product Pricing is required" : null;
  const partNumberRequiredError =
    isAdd && stemFilled && partNumber.trim().length === 0 ? "Part No is required" : null;
  /** `sherex_options.option_id` is varchar(30) — stem included. */
  const partNumberLengthError =
    isAdd && typedOptionId.length > 30
      ? `ID / Part No must be 30 characters or fewer (currently ${typedOptionId.length})`
      : null;
  /**
   * The composed id must land on the product the admin PICKED. The stems make
   * that true by construction, so this only fires on the awkward tails the
   * key rules break on (a letter straight after a FLEX stem) — and it fires on
   * svc-04's own answer, not on a second copy of the rule in the browser.
   */
  const pricingMismatchError =
    isAdd &&
    selectedChoice &&
    resolvedKey &&
    resolvedKey.toUpperCase() !== selectedChoice.productName.toUpperCase()
      ? `This ID resolves to pricing product "${resolvedKey}", not "${selectedChoice.productName}" — check the part number.`
      : null;

  const fillForm = (item: SherexOption) => {
    reset({
      optionId: item.optionId,
      productGroup: item.productGroup ?? "",
      threadSize: item.threadSize ?? "",
      installationMethod: item.installationMethod ?? "",
      pullingForce: item.pullingForce ?? "",
      availableStroke: item.availableStroke ?? "",
      airPressure: item.airPressure ?? "",
      airUse: item.airUse ?? "",
      toolSpeed: item.toolSpeed ?? "",
      weight: item.weight ?? "",
      completeHeadSetAssemblyPn: item.completeHeadSetAssemblyPn ?? "",
      mandrelPn: item.mandrelPn ?? "",
      anvilPn: item.anvilPn ?? "",
      completeSetUps: item.completeSetUps ?? "",
      includes: item.includes ?? "",
      price: item[priceKey] === null ? 0 : Number(item[priceKey]),
    });
  };

  /** Clears the Add-modal id builder; the other modes never use it. */
  const resetIdBuilder = () => {
    setPricingChoice("");
    setPartNumber("");
    setSaveAttempted(false);
  };

  const openAdd = () => {
    setFormError(null);
    resetIdBuilder();
    reset(blank);
    setModal({ mode: "add" });
  };
  const openEdit = (item: SherexOption) => {
    setFormError(null);
    resetIdBuilder();
    fillForm(item);
    setModal({ mode: "edit", item });
  };
  const openView = (item: SherexOption) => {
    setFormError(null);
    resetIdBuilder();
    fillForm(item);
    setModal({ mode: "view", item });
  };
  const closeModal = () => {
    setModal(null);
    setFormError(null);
    resetIdBuilder();
    reset(blank);
  };

  const onSubmit = (form: OptionFormData) => {
    if (!modal || modal.mode === "view") return;
    // Belt-and-braces: Save already refuses on a format miss, but the form also
    // submits on Enter. svc-04 rejects it either way.
    if (!idSatisfiesPattern(form.optionId)) return;
    if (addBlockers.length > 0) return;
    setFormError(null);

    const optionalFields = {
      threadSize: form.threadSize?.trim() || null,
      installationMethod: form.installationMethod?.trim() || null,
      pullingForce: form.pullingForce?.trim() || null,
      availableStroke: form.availableStroke?.trim() || null,
      airPressure: form.airPressure?.trim() || null,
      airUse: form.airUse?.trim() || null,
      toolSpeed: form.toolSpeed?.trim() || null,
      weight: form.weight?.trim() || null,
      completeHeadSetAssemblyPn: form.completeHeadSetAssemblyPn?.trim() || null,
      mandrelPn: form.mandrelPn?.trim() || null,
      anvilPn: form.anvilPn?.trim() || null,
      completeSetUps: form.completeSetUps?.trim() || null,
      includes: form.includes?.trim() || null,
    };
    const price = String(form.price);
    /** Only this mount's currency is ever sent — the sibling price column is
     *  owned by the other page and must not be written from here. */
    const pricePatch = currency === "USD" ? { priceUsd: price } : { priceEur: price };

    if (modal.mode === "edit" && modal.item) {
      // PATCH carries only what actually changed — optionId is immutable and
      // never sent.
      const current = modal.item;
      const patch: UpdateSherexOptionBody = {};
      const productGroup = form.productGroup.trim();
      if (productGroup !== (current.productGroup ?? "")) patch.productGroup = productGroup;
      if (optionalFields.threadSize !== current.threadSize) patch.threadSize = optionalFields.threadSize;
      if (optionalFields.installationMethod !== current.installationMethod)
        patch.installationMethod = optionalFields.installationMethod;
      if (optionalFields.pullingForce !== current.pullingForce)
        patch.pullingForce = optionalFields.pullingForce;
      if (optionalFields.availableStroke !== current.availableStroke)
        patch.availableStroke = optionalFields.availableStroke;
      if (optionalFields.airPressure !== current.airPressure)
        patch.airPressure = optionalFields.airPressure;
      if (optionalFields.airUse !== current.airUse) patch.airUse = optionalFields.airUse;
      if (optionalFields.toolSpeed !== current.toolSpeed) patch.toolSpeed = optionalFields.toolSpeed;
      if (optionalFields.weight !== current.weight) patch.weight = optionalFields.weight;
      if (optionalFields.completeHeadSetAssemblyPn !== current.completeHeadSetAssemblyPn)
        patch.completeHeadSetAssemblyPn = optionalFields.completeHeadSetAssemblyPn;
      if (optionalFields.mandrelPn !== current.mandrelPn) patch.mandrelPn = optionalFields.mandrelPn;
      if (optionalFields.anvilPn !== current.anvilPn) patch.anvilPn = optionalFields.anvilPn;
      if (optionalFields.completeSetUps !== current.completeSetUps)
        patch.completeSetUps = optionalFields.completeSetUps;
      if (optionalFields.includes !== current.includes) patch.includes = optionalFields.includes;
      if (Number(price) !== Number(current[priceKey] ?? NaN)) {
        Object.assign(patch, pricePatch);
      }

      if (Object.keys(patch).length === 0) {
        closeModal();
        return;
      }

      updateMutation.mutate(
        // Unscoped: either catalog's rows list here now. `region` is never
        // assigned anyway, so the row keeps the catalog it was added under.
        { optionId: current.optionId, body: patch },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save option.")),
        },
      );
    } else {
      createMutation.mutate(
        {
          // Sent verbatim — see the schema note on optionId.
          optionId: form.optionId,
          productGroup: form.productGroup.trim(),
          // Still stamped from the page it was added on (the column is NOT
          // NULL and immutable) — it just no longer decides where it lists.
          region: regionName,
          ...optionalFields,
          ...pricePatch,
        },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to create option.")),
        },
      );
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  /**
   * Read-only Product Name for VIEW/EDIT — `sherex_pricing.product_name`, the
   * pricing row this option resolves to. sherex_options has no such column and
   * there is no FK: the backend derives it from (optionId, productGroup) via
   * the canonical `getSherexPricingKey`, so it is READ from the preview/detail
   * payload and never recomputed in the browser (§R — no client-side business
   * rules). Add mode picks the product up front instead, and confirms the
   * resolved key under the ID field.
   */
  const resolvedProductName =
    pricePreview.data?.priceGroupKey ?? modal?.item?.priceGroupKey ?? "";
  const productNamePlaceholder = pricePreview.isFetching ? "Resolving…" : "—";

  /** The example part number for this group, i.e. its example id minus a stem. */
  const exampleTail = useMemo(() => {
    if (!activeIdPattern) return "";
    const { example, prefixes } = activeIdPattern;
    const stem = prefixes.find((p) =>
      example.toUpperCase().startsWith(p.toUpperCase()),
    );
    return stem ? example.slice(stem.length) : example;
  }, [activeIdPattern]);

  /** `sherex_pricing.product_name` is varchar(20); svc-04 rejects longer keys. */
  const productNameTooLong = isAdd && resolvedProductName.length > 20;

  /**
   * Everything that must be fixed before the POST is worth making. Save stays
   * CLICKABLE while these stand — pressing it is what reveals the required-field
   * messages (`saveAttempted`); a button that silently does nothing tells the
   * admin nothing.
   */
  const addBlockers = [
    idPatternError,
    pricingRequiredError,
    partNumberRequiredError,
    partNumberLengthError,
    pricingMismatchError,
    productNameTooLong ? "pricing-key-too-long" : null,
  ].filter((e): e is string => e !== null);

  const cols: ColDef<SherexOption>[] = [
    { header: "ID", render: (i) => <span className="font-semibold">{i.optionId}</span> },
    // sherex_options has NO product_name column — the Product Name IS the
    // resolved `sherex_pricing.product_name` (priceGroupKey), i.e. the pricing
    // row this option reads/writes. Same value the price column above is
    // sourced from, so the two always agree.
    { header: "Product Name", render: (i) => i.priceGroupKey || "—" },
    { header: "Product Group", render: (i) => <ProductGroupBadge value={i.productGroup} /> },
    { header: "Thread Size", render: (i) => i.threadSize ?? "—" },
    { header: "Install Method", render: (i) => <InstallMethodBadge value={i.installationMethod} /> },
    { header: "Tool Speed", render: (i) => i.toolSpeed ?? "—" },
    { header: "Weight", render: (i) => i.weight ?? "—" },
    {
      header: priceLabel,
      render: (i) => (
        // `title` carries the untouched numeric(12,4) string so the 2dp cell
        // is a display rounding, not a loss of the stored value (§L).
        <span
          className="whitespace-nowrap tabular-nums"
          title={i[priceKey] ?? undefined}
        >
          {formatMoney(i[priceKey], currency)}
        </span>
      ),
    },
    {
      header: "Actions",
      render: (i) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(i)} />
          <EditButton onClick={() => openEdit(i)} />
          <DeleteButton
            onClick={() => setDeleteTarget(i)}
            disabled={deleteMutation.isPending}
            disabledTitle="Delete in progress"
          />
        </div>
      ),
    },
  ];

  // Region is part of the modal title so the admin can never lose track of
  // which mount (EU / NA price column) they are writing to.
  const titleMap: Record<ModalMode, string> = {
    add: `Add Sherex Options (${regionName})`,
    edit: `Edit Sherex Options (${regionName})`,
    view: `View Sherex Options (${regionName})`,
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={`${subtitle} · ${total} records`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => { setBulkError(null); setPriceChangeOpen(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-penn-500 hover:bg-penn-700 active:bg-penn-500 text-white rounded-lg shadow-sm shadow-penn-100 dark:shadow-penn-900/40 transition-all duration-150 cursor-pointer text-body-medium"
            >
              <Percent size={15} />
              Change Bulk Price
            </button>
            <AddButton onClick={openAdd} label="Add record" />
          </div>
        }
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load sherex options
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search id..."
      />

      <AdminTable
        columns={cols}
        rows={rows}
        keyExtractor={(i) => i.optionId}
        loading={isLoading}
      />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-2xl"
        footer={
          isView ? (
            <CancelButton onClick={closeModal} />
          ) : (
            <>
              <CancelButton onClick={closeModal} />
              {/* Refuses while anything in `addBlockers` stands — saving before
                  that prices the option off a row the admin never chose, or one
                  nothing prices at all. The click still lands so the pending
                  required-field messages appear. */}
              <SaveButton
                onClick={() => {
                  if (saving) return;
                  setSaveAttempted(true);
                  if (addBlockers.length > 0) return;
                  void handleSubmit(onSubmit)();
                }}
                label={saving ? "Saving…" : "Save"}
              />
            </>
          )
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}

            {/* Product Group is deliberately the FIRST field — it unlocks the
                rest of the form and decides which ID / Part No formats are
                accepted (see `activeIdPattern`). It is also ADD-ONLY: the id was
                written to this group's format and the pricing row is resolved
                from (id, group), so re-pointing the group on an existing row
                would leave the id spelling behind and silently move the option
                onto another price — svc-04 rejects that PATCH outright. Change
                the group by creating the option under the right one instead. */}
            <PennField
              label="Product Group"
              htmlFor="productGroup"
              required={isAdd}
              error={!isView ? errors.productGroup?.message : undefined}
              hint={
                modal?.mode === "edit"
                  ? "Locked after create — it decides the ID and the pricing product."
                  : undefined
              }
            >
              <Controller
                control={control}
                name="productGroup"
                render={({ field }) => (
                  <PennDropdown
                    inputId="productGroup"
                    options={groupOptions.map((g) => ({ value: g, label: g }))}
                    value={field.value}
                    onChange={(next) => {
                      field.onChange(next);
                      if (!isAdd) return;
                      // The new group has its own pricing products and its own
                      // id format, so the whole id builder is cleared rather
                      // than left pointing at the previous group's stem.
                      resetIdBuilder();
                      setValue("optionId", "", { shouldValidate: false });
                    }}
                    onBlur={field.onBlur}
                    isDisabled={!isAdd}
                    placeholder="Select group..."
                  />
                )}
              />
            </PennField>


            {/* ADD picks the pricing row up front; VIEW/EDIT can only read the
                one already resolved (both the id and the group are locked after
                create, so it can never change there). */}
            {isAdd ? (
              <PennField
                label="Product Name / Product Pricing"
                htmlFor="pricingProduct"
                required
                error={
                  saveAttempted ? pricingRequiredError ?? undefined : undefined
                }
              >
                <PennDropdown
                  inputId="pricingProduct"
                  isSearchable
                  options={pricingChoices.map((choice) => {
                    // Just the product name. The stem is appended only when the
                    // product has more than one (FLEX5), where it is the only
                    // thing telling two entries apart. The row's price is not
                    // repeated here — the Price field below shows it, seeded
                    // from whichever product is picked.
                    const ambiguous =
                      pricingChoices.filter(
                        (c) => c.productName === choice.productName,
                      ).length > 1;
                    return {
                      value: choice.stem,
                      label: ambiguous
                        ? `${choice.productName} (${choice.stem.trim()})`
                        : choice.productName,
                    };
                  })}
                  value={pricingChoice}
                  onChange={setPricingChoice}
                  isDisabled={!groupSelected}
                  isLoading={priceProductsQuery.isFetching}
                  error={saveAttempted && pricingRequiredError !== null}
                  placeholder={
                    groupSelected
                      ? "Select pricing product..."
                      : "Select a Product Group first"
                  }
                />
              </PennField>
            ) : (
              <PennField
                label="Product Name / Product Pricing"
                htmlFor="productName"
                hint="Product Pricing -Auto derived from Product Group + ID / Part No ."
              >
                {/* readOnly, not disabled — the admin must be able to read and
                    copy the resolved group, and a disabled input renders at
                    opacity-50. It is not registered with RHF: the backend owns
                    this value and never accepts it from the client. */}
                <PennInput
                  id="productName"
                  readOnly
                  value={resolvedProductName}
                  placeholder={productNamePlaceholder}
                  className="cursor-default bg-gray-100 dark:bg-gray-800 focus:border-gray-200"
                />
              </PennField>
            )}

            <div className="col-span-1 sm:col-span-2">
              {/* ADD composes the id: the chosen pricing product's stem is
                  locked in front, and the admin types only the part number
                  after it. That is what guarantees the new option joins the
                  price row that was actually picked — the two tables have no FK,
                  so a free-typed stem could quietly land it anywhere. svc-04
                  re-checks the same format on POST (§L). */}
              <PennField
                label="ID / Part No"
                htmlFor="optionId"
                required={!isView}
                error={
                  isAdd
                    ? ((saveAttempted ? partNumberRequiredError : null) ??
                        partNumberLengthError ??
                        idPatternError ??
                        pricingMismatchError ??
                        errors.optionId?.message) ?? undefined
                    : undefined
                }
                hint={
                  isAdd && stemFilled
                    ? `Type the part number that follows ${activeStem.trim()}${
                        exampleTail ? `. Example: ${exampleTail.trim()}` : ""
                      }`
                    : undefined
                }
              >
                {isAdd ? (
                  <div className="flex items-stretch">
                    {/* `whitespace-pre`, not trimmed: several stems END in a
                        separator that is part of the id ("DLHT ", "FLEX-5P "),
                        and hiding it would invite the admin to type it twice. */}
                    <span className="inline-flex items-center whitespace-pre rounded-l-xl border border-r-0 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800 px-3 font-mono text-blue_grey3 text-body">
                      {activeStem || "—"}
                    </span>
                    {/* Not registered with RHF: `optionId` holds the composed
                        value (see the effect above), this input holds the tail. */}
                    <PennInput
                      id="optionId"
                      className="rounded-l-none"
                      placeholder={
                        !groupSelected
                          ? "Select a Product Group first"
                          : !stemFilled
                            ? "Select a pricing product first"
                            : exampleTail
                              ? `e.g. ${exampleTail.trim()}`
                              : "e.g. 04-1440"
                      }
                      value={partNumber}
                      onChange={(e) => setPartNumber(e.target.value)}
                      disabled={fieldsDisabled || !stemFilled}
                      error={
                        (saveAttempted && partNumberRequiredError !== null) ||
                        partNumberLengthError !== null ||
                        idPatternError !== null ||
                        pricingMismatchError !== null
                      }
                    />
                  </div>
                ) : (
                  <PennInput
                    id="optionId"
                    // Immutable after create — the id is the natural PK.
                    disabled
                    {...register("optionId")}
                  />
                )}
              </PennField>
              {/* The id that will actually be stored, plus the pricing row
                  svc-04 resolves it to — the server's answer, not a guess. */}
              {isAdd && stemFilled && partNumber.trim().length > 0 && (
                <p className="mt-2 text-blue_grey3 text-small">
                  Full ID: <span className="font-mono">{typedOptionId}</span>
                  {resolvedKey ? (
                    <>
                      {" → pricing product "}
                      <strong>{resolvedKey}</strong>
                    </>
                  ) : pricePreview.isFetching ? (
                    " · resolving pricing product…"
                  ) : null}
                </p>
              )}
              {productNameTooLong && (
                <p className="mt-2 text-error text-small">
                  Pricing product names are limited to 20 characters — shorten the
                  part number.
                </p>
              )}
            </div>

            <PennField
              label="Thread Size"
              htmlFor="threadSize"
              error={!isView ? errors.threadSize?.message : undefined}
            >
              <PennInput
                id="threadSize"
                placeholder="e.g. M6, 1/4-20"
                disabled={fieldsDisabled}
                error={!isView && !!errors.threadSize}
                {...register("threadSize")}
              />
            </PennField>

            <PennField
              label="Installation Method"
              htmlFor="installationMethod"
              error={!isView ? errors.installationMethod?.message : undefined}
            >
              <PennInput
                id="installationMethod"
                placeholder="e.g. Spin-Spin"
                disabled={fieldsDisabled}
                error={!isView && !!errors.installationMethod}
                {...register("installationMethod")}
              />
            </PennField>

            <PennField
              label="Pulling Force"
              htmlFor="pullingForce"
              error={!isView ? errors.pullingForce?.message : undefined}
            >
              <PennInput
                id="pullingForce"
                placeholder="e.g. 5,000 lbs"
                disabled={fieldsDisabled}
                error={!isView && !!errors.pullingForce}
                {...register("pullingForce")}
              />
            </PennField>

            <PennField
              label="Available Stroke"
              htmlFor="availableStroke"
              error={!isView ? errors.availableStroke?.message : undefined}
            >
              <PennInput
                id="availableStroke"
                placeholder="e.g. 7 mm (0.276 in)"
                disabled={fieldsDisabled}
                error={!isView && !!errors.availableStroke}
                {...register("availableStroke")}
              />
            </PennField>

            <PennField
              label="Air Pressure"
              htmlFor="airPressure"
              error={!isView ? errors.airPressure?.message : undefined}
            >
              <PennInput
                id="airPressure"
                placeholder="e.g. 70-80 psi (4.8-5.5 bars)"
                disabled={fieldsDisabled}
                error={!isView && !!errors.airPressure}
                {...register("airPressure")}
              />
            </PennField>

            <PennField
              label="Air Use"
              htmlFor="airUse"
              error={!isView ? errors.airUse?.message : undefined}
            >
              <PennInput
                id="airUse"
                placeholder="e.g. 5 cfm"
                disabled={fieldsDisabled}
                error={!isView && !!errors.airUse}
                {...register("airUse")}
              />
            </PennField>

            <PennField
              label="Tool Speed"
              htmlFor="toolSpeed"
              error={!isView ? errors.toolSpeed?.message : undefined}
            >
              <PennInput
                id="toolSpeed"
                placeholder="e.g. 3000 rpm"
                disabled={fieldsDisabled}
                error={!isView && !!errors.toolSpeed}
                {...register("toolSpeed")}
              />
            </PennField>

            <PennField
              label="Weight"
              htmlFor="weight"
              error={!isView ? errors.weight?.message : undefined}
            >
              <PennInput
                id="weight"
                placeholder="e.g. 3.0 lbs (1.4 kg)"
                disabled={fieldsDisabled}
                error={!isView && !!errors.weight}
                {...register("weight")}
              />
            </PennField>

            <PennField
              label="Complete Head Set PN"
              htmlFor="completeHeadSetAssemblyPn"
              error={!isView ? errors.completeHeadSetAssemblyPn?.message : undefined}
            >
              <PennInput
                id="completeHeadSetAssemblyPn"
                placeholder="e.g. FL5-HS-0440"
                disabled={fieldsDisabled}
                error={!isView && !!errors.completeHeadSetAssemblyPn}
                {...register("completeHeadSetAssemblyPn")}
              />
            </PennField>

            <PennField
              label="Mandrel PN"
              htmlFor="mandrelPn"
              error={!isView ? errors.mandrelPn?.message : undefined}
            >
              <PennInput
                id="mandrelPn"
                placeholder="e.g. M-0440-200"
                disabled={fieldsDisabled}
                error={!isView && !!errors.mandrelPn}
                {...register("mandrelPn")}
              />
            </PennField>

            <PennField
              label="Anvil PN"
              htmlFor="anvilPn"
              error={!isView ? errors.anvilPn?.message : undefined}
            >
              <PennInput
                id="anvilPn"
                placeholder="e.g. MA-2520"
                disabled={fieldsDisabled}
                error={!isView && !!errors.anvilPn}
                {...register("anvilPn")}
              />
            </PennField>

            <PennField
              label="Complete Set-Ups"
              htmlFor="completeSetUps"
              error={!isView ? errors.completeSetUps?.message : undefined}
            >
              <PennInput
                id="completeSetUps"
                placeholder="e.g. MHS-2520"
                disabled={fieldsDisabled}
                error={!isView && !!errors.completeSetUps}
                {...register("completeSetUps")}
              />
            </PennField>

            <div className="col-span-1 sm:col-span-2">
              <PennField
                label="Includes"
                htmlFor="includes"
                error={!isView ? errors.includes?.message : undefined}
              >
                <PennInput
                  id="includes"
                  placeholder="optional"
                  disabled={fieldsDisabled}
                  error={!isView && !!errors.includes}
                  {...register("includes")}
                />
              </PennField>
            </div>

            <div className="col-span-1 sm:col-span-2">
              <PennField
                label={priceLabel}
                htmlFor="price"
                required={!isView}
                error={!isView ? errors.price?.message : undefined}
              >
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-blue_grey4 text-body">
                    {currencySymbol}
                  </span>
                  <PennInput
                    id="price"
                    type="number"
                    step="0.0001"
                    min="0"
                    className="pl-7"
                    disabled={fieldsDisabled}
                    error={!isView && !!errors.price}
                    {...register("price")}
                  />
                </div>
              </PennField>
              {!isView && (
                <SharedPriceNote preview={pricePreview} currency={currency} />
              )}
            </div>
          </form>
        )}
      </AdminModal>

      <ChangeSalesPriceModal
        open={priceChangeOpen}
        onClose={() => setPriceChangeOpen(false)}
        title={`% Change Bulk Price — Sherex Options (${regionName})`}
        currency={currency}
        scopeLabel={`Sherex Options (${regionName})`}
        recordCount={priceSummary.data?.totalRecords ?? 0}
        recordNoun={{ one: "price", many: "prices" }}
        descriptionSuffix={`Only the ${currency} column moves. Sherex prices are shared: one pricing product covers every option in its group, so a change reprices all of them.`}
        applying={applyPriceMutation.isPending}
        error={bulkError}
        onApply={applyPriceChange}
        lastUpdate={priceSummary.data?.last}
        loadingSummary={priceSummary.isLoading}
      />

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(
            { optionId: deleteTarget.optionId },
            {
              onError: (e) => setDeleteError(apiErrorMessage(e, "Failed to delete option.")),
              onSettled: () => setDeleteTarget(null),
            },
          );
        }}
        itemLabel={deleteTarget?.optionId}
      />
    </>
  );
};
